import { Formik, ErrorMessage } from 'formik'
import { useRouter } from 'next/router'
import { useCallback, useState } from 'react'

import { useEffectOnce } from 'react-use'
import { validateCPF, validateCNPJ } from 'validations-br'

import * as Yup from 'yup'

import {
  useSelectButtonEvent,
  useSelectFieldEvent,
  useSelectLinkEvent,
} from '@/core/analytics/hooks'
import { CookieStorage } from '@/core/utils/cookies'

import type { ConsultData } from '../types'

import { Form, FormInput, FormSubmitButton, FormWrapper } from './styles'

const CONSULT_TYPE = {
  cpf: 'CONSULTAR_CPF',
  cnpj: 'CONSULTAR_CNPJ',
}

export const FreeDocumentConsultingForms = ({ consultData }: { consultData?: ConsultData }) => {
  const { push } = useRouter()
  const [queryParams, setQueryParams] = useState('')
  const selectFieldEvent = useSelectFieldEvent({ itemId: '' })
  const selectButtonEvent = useSelectButtonEvent({ itemId: '' })
  const selectContentEvent = useSelectLinkEvent()

  const consultDocument = useCallback(
    (document: string, type: 'cpf' | 'cnpj') => {
      CookieStorage.set('S_VCEVCP_DOCUMENT', document)
      push(`/voceconsulta/dados-checkout/${CONSULT_TYPE[type]}/${queryParams}`)
    },
    [push, queryParams],
  )

  const cpfEvents = useCallback(() => {
    selectFieldEvent({ label: 'cpf-a-ser-consultado' })
    selectButtonEvent({ label: 'consultar-cpf' })
  }, [selectFieldEvent, selectButtonEvent])

  const cnpjEvents = useCallback(() => {
    selectFieldEvent({ label: 'cnpj-empresa' })
    selectButtonEvent({ label: 'consultar-cnpj' })
  }, [selectFieldEvent, selectButtonEvent])

  const handleSubmit = useCallback(
    (values: { cpf?: string; cnpj?: string; type: 'cpf' | 'cnpj' }) => {
      const document = values.cpf || values.cnpj
      if (!document) return

      if (document.replace('-', '').replaceAll('.', '') == consultData?.document) return

      const isValid = validateCPF(document) || validateCNPJ(document)
      if (isValid) {
        values.type === 'cpf' ? cpfEvents() : cnpjEvents()
        consultDocument(document, values.type)
      }
    },
    [consultDocument, cpfEvents, cnpjEvents, consultData],
  )

  const validationSchemaCpf = Yup.object().shape({
    cpf: Yup.string()
      .required('O CPF é obrigatório')
      .test('valid-cpf', 'Digite um CPF válido', (value) => validateCPF(`${value}`)),
  })

  const validationSchemaCnpj = Yup.object().shape({
    cnpj: Yup.string()
      .required('O CNPJ é obrigatório')
      .test('valid-cnpj', 'Digite um CNPJ válido', (value) => validateCNPJ(`${value}`)),
  })

  const handleClickLink = () => {
    selectContentEvent({ label: 'site-da-serasa' })
  }

  useEffectOnce(() => {
    setQueryParams(window.location.search)
  })

  return (
    <div className="media-with-forms__inputs">
      <FormWrapper>
        <Formik
          validationSchema={validationSchemaCpf}
          initialValues={{ cpf: '', type: 'cpf' }}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit, values, errors }) => (
            <div>
              <Form onSubmit={handleSubmit}>
                <FormInput
                  name="cpf"
                  placeholder="CPF a ser consultado"
                  value={values.cpf}
                  mask="cpf"
                  type="tel"
                />

                <FormSubmitButton type="submit" aria-label="Consultar CPF inserido">
                  Consultar
                </FormSubmitButton>
              </Form>
              {errors.cpf && <ErrorMessage name="cpf" component="span" className="error-message" />}
              <br></br>
              {!errors.cpf &&
                values.cpf?.replace('-', '').replaceAll('.', '') != consultData?.document && (
                  <p style={{ fontSize: '0.75rem' }}>
                    Consulte seu próprio CPF gratuitamente no{' '}
                    <a
                      href="https://www.serasa.com.br/area-cliente/meu-cpf"
                      onClick={handleClickLink}
                      rel="noopener noreferrer"
                      style={{ color: '#E80170' }}
                    >
                      <u>site da Serasa</u>
                    </a>
                  </p>
                )}
              {!errors.cpf &&
                values.cpf?.replace('-', '').replaceAll('.', '') == consultData?.document && (
                  <p style={{ fontSize: '0.75rem', color: '#D6103B' }}>
                    {consultData?.isPremium
                      ? 'Vimos que você é assinante Premium. Consulte seu CPF no '
                      : 'Consulte seu próprio CPF gratuitamente no '}
                    <a
                      href="https://www.serasa.com.br/area-cliente/meu-cpf"
                      onClick={handleClickLink}
                      rel="noopener noreferrer"
                    >
                      <u>site da Serasa</u>
                    </a>
                  </p>
                )}
            </div>
          )}
        </Formik>

        <Formik
          validationSchema={validationSchemaCnpj}
          initialValues={{ cnpj: '', type: 'cnpj' }}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit, values }) => (
            <div>
              <Form onSubmit={handleSubmit}>
                <FormInput
                  name="cnpj"
                  placeholder="CNPJ a ser consultado"
                  value={values.cnpj}
                  mask="cnpj"
                  type="tel"
                />
                <FormSubmitButton type="submit" aria-label="Consultar CNPJ inserido">
                  Consultar
                </FormSubmitButton>
              </Form>
              <ErrorMessage name="cnpj" component="span" className="error-message" />
            </div>
          )}
        </Formik>
      </FormWrapper>
    </div>
  )
}
